/**
 * Accessible Modal Component
 * Provides a reusable, accessible modal system
 */

import { devLog } from './utils.js';

class AccessibleModal {
    constructor(modalId, options = {}) {
        this.modalId = modalId;
        this.modal = null;
        this.options = {
            closeOnBackdrop: true,
            closeOnEscape: true,
            focusOnOpen: true,
            animationDuration: 300,
            ...options
        };
        
        this.previousFocus = null;
        this.isOpen = false;
        this.focusableElements = [];
        this.firstFocusable = null;
        this.lastFocusable = null;
        
        // Bind methods to maintain context
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
        this.close = this.close.bind(this);
        
        this.init();
    }

    init() {
        this.createModalHTML();
        this.cacheElements();
        this.attachEventListeners();
        devLog('Modal initialized:', this.modalId);
    }

    createModalHTML() {
        // Remove existing modal if it exists
        const existingModal = document.getElementById(this.modalId);
        if (existingModal) {
            existingModal.remove();
        }

        const modalHTML = `
            <div id="${this.modalId}" class="modal" aria-hidden="true" role="dialog" aria-modal="true" aria-labelledby="${this.modalId}-title">
                <div class="modal__backdrop" data-modal-close></div>
                <div class="modal__container">
                    <div class="modal__header">
                        <h2 class="modal__title" id="${this.modalId}-title">Modal Title</h2>
                        <button class="modal__close" data-modal-close aria-label="Close modal" type="button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                    <div class="modal__body" id="${this.modalId}-content">
                        <!-- Dynamic content will be inserted here -->
                    </div>
                    <div class="modal__footer" id="${this.modalId}-footer" style="display: none;">
                        <!-- Optional footer content -->
                    </div>
                </div>
            </div>
        `;

        // Append to body
        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    cacheElements() {
        this.modal = document.getElementById(this.modalId);
        this.backdrop = this.modal.querySelector('.modal__backdrop');
        this.container = this.modal.querySelector('.modal__container');
        this.closeButton = this.modal.querySelector('.modal__close');
        this.titleElement = this.modal.querySelector('.modal__title');
        this.bodyElement = this.modal.querySelector('.modal__body');
        this.footerElement = this.modal.querySelector('.modal__footer');
    }

    attachEventListeners() {
        // Close button clicks
        this.modal.querySelectorAll('[data-modal-close]').forEach(element => {
            element.addEventListener('click', this.close);
        });

        // Prevent container clicks from closing modal
        this.container.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    open(content = '', title = '', footerContent = '') {
        if (this.isOpen) {
            devLog('Modal already open');
            return;
        }

        // Store currently focused element
        this.previousFocus = document.activeElement;

        // Set content
        if (title) {
            this.titleElement.textContent = title;
        }
        
        if (content) {
            this.bodyElement.innerHTML = content;
        }

        if (footerContent) {
            this.footerElement.innerHTML = footerContent;
            this.footerElement.style.display = 'block';
        } else {
            this.footerElement.style.display = 'none';
        }

        // Show modal
        this.modal.classList.add('modal--active');
        this.modal.setAttribute('aria-hidden', 'false');
        this.isOpen = true;

        // Manage focus and events
        this.setupFocusManagement();
        this.addEventListeners();

        // Focus management
        if (this.options.focusOnOpen) {
            setTimeout(() => {
                this.focusFirstElement();
            }, 50); // Small delay to ensure modal is visible
        }

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Announce to screen readers
        this.announceToScreenReader(`Modal opened: ${title || 'Dialog'}`);

        devLog('Modal opened:', this.modalId);
    }

    close() {
        if (!this.isOpen) {
            return;
        }

        // Hide modal
        this.modal.classList.remove('modal--active');
        this.modal.setAttribute('aria-hidden', 'true');
        this.isOpen = false;

        // Clean up
        this.removeEventListeners();

        // Restore focus
        if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
            setTimeout(() => {
                this.previousFocus.focus();
            }, this.options.animationDuration);
        }

        // Restore body scroll
        document.body.style.overflow = '';

        // Announce to screen readers
        this.announceToScreenReader('Modal closed');

        devLog('Modal closed:', this.modalId);
    }

    setupFocusManagement() {
        // Find all focusable elements within the modal
        const focusableSelector = [
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        this.focusableElements = Array.from(this.modal.querySelectorAll(focusableSelector));
        this.firstFocusable = this.focusableElements[0];
        this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }

    focusFirstElement() {
        if (this.firstFocusable) {
            this.firstFocusable.focus();
        } else {
            // Fallback to close button
            this.closeButton.focus();
        }
    }

    addEventListeners() {
        if (this.options.closeOnEscape) {
            document.addEventListener('keydown', this.handleKeydown);
        }
        if (this.options.closeOnBackdrop) {
            this.backdrop.addEventListener('click', this.handleBackdropClick);
        }
    }

    removeEventListeners() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.backdrop.removeEventListener('click', this.handleBackdropClick);
    }

    handleKeydown(e) {
        if (!this.isOpen) return;

        switch (e.key) {
            case 'Escape':
                if (this.options.closeOnEscape) {
                    e.preventDefault();
                    this.close();
                }
                break;

            case 'Tab':
                this.handleTabKey(e);
                break;
        }
    }

    handleTabKey(e) {
        if (this.focusableElements.length === 0) return;

        // If only one focusable element, prevent tabbing
        if (this.focusableElements.length === 1) {
            e.preventDefault();
            return;
        }

        // Trap focus within modal
        if (e.shiftKey) {
            // Shift + Tab (going backward)
            if (document.activeElement === this.firstFocusable) {
                e.preventDefault();
                this.lastFocusable.focus();
            }
        } else {
            // Tab (going forward)
            if (document.activeElement === this.lastFocusable) {
                e.preventDefault();
                this.firstFocusable.focus();
            }
        }
    }

    handleBackdropClick(e) {
        if (e.target === this.backdrop) {
            this.close();
        }
    }

    announceToScreenReader(message) {
        // Create a temporary element for screen reader announcements
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.style.position = 'absolute';
        announcement.style.left = '-9999px';
        announcement.style.width = '1px';
        announcement.style.height = '1px';
        announcement.style.overflow = 'hidden';
        
        document.body.appendChild(announcement);
        announcement.textContent = message;
        
        // Remove after announcement
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    }

    // Public methods for content management
    setTitle(title) {
        this.titleElement.textContent = title;
    }

    setContent(content) {
        this.bodyElement.innerHTML = content;
        if (this.isOpen) {
            this.setupFocusManagement();
        }
    }

    setFooter(footerContent) {
        this.footerElement.innerHTML = footerContent;
        this.footerElement.style.display = footerContent ? 'block' : 'none';
    }

    appendContent(content) {
        this.bodyElement.insertAdjacentHTML('beforeend', content);
        if (this.isOpen) {
            this.setupFocusManagement();
        }
    }

    // Utility method to check if modal is open
    get isModalOpen() {
        return this.isOpen;
    }

    // Destroy method for cleanup
    destroy() {
        this.removeEventListeners();
        if (this.modal && this.modal.parentNode) {
            this.modal.parentNode.removeChild(this.modal);
        }
        devLog('Modal destroyed:', this.modalId);
    }
}

export default AccessibleModal;
